import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FlaskConical, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("No verification token provided");
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/verify?token=${token}`, {
          credentials: "include"
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.detail || "Verification failed");
        }
        
        setStatus("success");
        toast.success("Email verified successfully!");
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate("/dashboard", { state: { user: data } });
        }, 2000);
      } catch (err) {
        setStatus("error");
        setError(err.message || "Verification failed");
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center">
            <FlaskConical className="w-7 h-7 text-white" />
          </div>
          <span className="text-2xl font-bold text-slate-900">BioMatch AI</span>
        </div>

        <Card className="border border-slate-200 shadow-lg">
          <CardContent className="p-8 text-center">
            {status === "verifying" && (
              <>
                <Loader2 className="w-16 h-16 text-teal-500 animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  Verifying Your Email
                </h2>
                <p className="text-slate-500">
                  Please wait while we verify your email address...
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-16 h-16 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-teal-500" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  Email Verified!
                </h2>
                <p className="text-slate-500 mb-6">
                  Your email has been verified successfully. Redirecting to dashboard...
                </p>
                <Loader2 className="w-5 h-5 text-teal-500 animate-spin mx-auto" />
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900 mb-2">
                  Verification Failed
                </h2>
                <p className="text-slate-500 mb-6">
                  {error}
                </p>
                <div className="space-y-3">
                  <Link to="/auth">
                    <Button className="w-full btn-secondary">
                      Back to Sign In
                    </Button>
                  </Link>
                  <p className="text-sm text-slate-500">
                    Need a new verification link? Sign in and request a new one.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
